import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Paper, Stack, Grid, CircularProgress, Card
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';

const Bank = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ bankname: '', code: '', branch: '' });
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  // Fetch the current user's schoolId
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSchoolId(data.schoolId);
      } else {
        toast.error("User not found");
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      toast.error("Error fetching user");
    }
  };

  // Fetch banks for this school
  const fetchBanks = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'banks'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBanks(data);
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchSchoolId();
    };
    init();
  }, []);

  useEffect(() => {
    if (schoolId) fetchBanks(schoolId);
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ bankname: '', code: '', branch: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { bankname, code, branch } = formData;

    if (!bankname.trim() ) {
      toast.error("All fields are required");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    try {
      const bankData = { bankname, code, branch, schoolId };

      if (editMode && editId) {
        await updateDoc(doc(db, 'banks', editId), bankData);
        toast.success("Bank updated");
      } else {
        await addDoc(collection(db, 'banks'), bankData);
        toast.success("Bank added");
      }

      fetchBanks(schoolId);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (bank) => {
    setFormData({
      bankname: bank.bankname || '',
      code: bank.code || '',
      branch: bank.branch || ''
    });
    setEditId(bank.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'banks', id));
      toast.success("Bank deleted");
      fetchBanks(schoolId);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting bank");
    }
  };

  const paginated = banks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Bank</Button>
        <Typography variant="h5" fontWeight="bold">Banks</Typography>
      </Stack>

      <Card>
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Branch</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No banks found</TableCell>
                </TableRow>
              ) : (
                paginated.map((bank, index) => (
                  <TableRow key={bank.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(bank)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(bank.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{bank.bankname}</TableCell>
                    <TableCell>{bank.code}</TableCell>
                    <TableCell>{bank.branch}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={banks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Bank</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Name"
                name="bankname"
                value={formData.bankname}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Branch"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bank;